import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from 'store/surveys';
import { AssociatedCampaignsModalDialog } from 'views/UserMessaging/customModalContent/associatedCampaigns';
import { actions as userMessageActions } from 'store/userMessaging';
import useSurvey from './useSurvey';
import { TableComponentV2, ToolbarV2 } from 'components';
import filterHelpers from 'store/helpers/filterHelpers';
import { surveyTableTitles } from 'constants.js';
import { Typography } from '@material-ui/core';
import PermissionCheck from 'PermissionsCheck.js';
import { resetListsData } from 'store/lists/actions';
import { getEnvVar } from 'helpers';
import CopyTemplateModal from 'components/Template/CopyTemplateModal';

const {
  getPaginatedSurveys,
  deleteSurveys,
  unsetCurrentSurvey,
  setCurrentSurvey,
  setCopyCurrentSurvey,
  updateEventIdWithoutEffects,
  updateSurveyFilters,
  resetSurveyFilters
} = actions;

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  }
}));

const Surveys = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    onClickToggleArchive,
    onClickAdd,
    onClickEdit,
    onClickCopy,
    isTemplateReadOnly,
    isTenantDefaultTemplate
  } = filterHelpers();
  const permissions = PermissionCheck();
  const { hasPermission } = permissions;
  const currentBusinessContextId = useSelector(
    store => store.user.selectedBusinessContext.businessContextId
  );
  const action = getPaginatedSurveys({ force: true });
  const currentEventId = useSelector(store => store.survey.eventId);
  const surveys = useSelector(store => store.survey.paginatedList);
  const filters = useSelector(
    store => store.survey.filters
  );
  const isLoading = useSelector(store => store.survey.gettingPaginated);
  const productNames = useSelector(
    store => store.events.supportedEventTypes.transientProductNames
  );
  const productCategories = useSelector(
    store => store.events.supportedEventTypes.transientProductCategories
  );
  const optionsToFilterTemplate = [
    {
      name: 'searchScopes',
      searchScope: 'TENANT DEFAULT',
      value: 'tenant',
      checked: false
    },
    {
      name: 'searchScopes',
      searchScope: 'PROPERTY LEVEL',
      value: 'property',
      checked: false
    }
  ];
  const optionsToFilterProductNames = productNames
    ? productNames
        .filter(
          productName =>
            productName !== 'ListManagement' ||
            getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT_PHASE2') === 'true'
        )
        .filter(
          productName =>
            productName !== 'GuestInsight'
        )
        .map(productName => {
          return { name: 'productName', value: productName, checked: false };
        })
    : [];
  const optionsToFilterProductCategories = productCategories
    ? productCategories
        .filter(
          productCategory =>
            productCategory !== 'ADM' ||
            getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT_PHASE2') === 'true'
        )
        .map(productCategory => {
          return {
            name: 'productCategory',
            value: productCategory,
            checked: false
          };
        })
    : [];

  const [hasReadPermission, setHasReadPermission] = useState(false);
  const [openCopyTemplateModal, setOpenCopyTemplateModal] = useState(false);

  const survey = useSurvey();
  const { handleSurveyPermissions } = survey;

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (currentEventId) {
      dispatch(updateEventIdWithoutEffects(null));
    }
    if (handleSurveyPermissions('Rguest_readSurvey')) {
      setHasReadPermission(true);
      dispatch(getPaginatedSurveys({ force: true }));
    }
    dispatch(resetListsData());
  }, [currentBusinessContextId]);

  const getTooltipTitle = (survey) => {
    return isTenantDefaultTemplate(survey)
      ? "Tenant Default Survey can be edited only with 'ALL' property"
      : 'Only admin can edit Global Default Survey';
  }

  const getActionButtons = survey => {
  const actionButton = [];
  if (hasPermission('Rguest_writeSurvey')) {
    actionButton.push(
      ...[
        {
          name: isTemplateReadOnly(survey, isLoading)
            ? 'View Survey'
            : 'Edit Survey',
          action: ['ACTION', () => onEdit(survey)],
          ...(isTemplateReadOnly(survey, isLoading) && {
            tooltipPlacement: 'left-start',
            tooltipTitle: getTooltipTitle(survey)
          })
        },
        {
          name: filters?.displayActive ? 'Archive Survey' : 'Activate Survey',
          action: ['ACTION', () => onClickArchive(survey.id)],
          ...(isTemplateReadOnly(survey, isLoading) && {
            isDisabled: true,
            tooltipPlacement: 'left-start',
            tooltipTitle: getTooltipTitle(survey)
          })
        },
        {
          name: 'Copy Survey',
          action: ['ACTION', () => onCopy(survey.id)]
        },
        {
          name: 'View Individual Survey Results',
          action: [
            'GO',
            `/surveys/results/${survey.id}/${survey.surveyHash}/response`
          ]
        }
      ]
    );
  } else {
    actionButton.push(
      ...[
        {
          name: 'View Survey',
          action: ['ACTION', () => onEdit(survey)],
          ...(isTemplateReadOnly(survey, isLoading) && {
            tooltipPlacement: 'left-start',
            tooltipTitle: isTenantDefaultTemplate(survey)
              ? "Tenant Default Survey can be edited only with 'ALL' property"
              : 'Only admin can edit Global Default Survey'
          })
        }
      ]
    );
  }
  if (hasPermission('Rguest_readSurveyResult')) {
    actionButton.push({
      name: 'View Survey Results',
      action: ['GO', `/surveys/results/${survey.id}/${survey.surveyHash}`]
    });
  }
  return actionButton;
};

  const onClickArchive = id => {
    let action = deleteSurveys({
      surveyIds: [id],
      activeStatus: filters?.displayActive
    });
    let warnMessage = {
      buttons: [
        { title: 'Archive Survey', action: ['DISPATCH', action] },
        { title: 'Cancel', action: 'DISMISS' }
      ],
      customContent: AssociatedCampaignsModalDialog,
      props: {
        template: { associatedCampaigns: [] },
        type: 'surveys'
      }
    };
    if (filters?.displayActive) {
      dispatch(userMessageActions.setMessage(warnMessage));
    } else {
      dispatch(action);
    }
  };

  const handleImportClick = () => {
    setOpenCopyTemplateModal(true);
  };

  const getButtonsGroup = () => [
    {
      name: `View ${filters?.displayActive ? 'Archived' : 'Active'} Surveys`,
      action: () => onClickToggleArchive(action, updateSurveyFilters, { displayActive: !filters?.displayActive, pageNo: 0 })
    },
    {
      name: 'Add Survey',
      action: () => onClickAdd(unsetCurrentSurvey(), `/surveys/details`)
    },
    {
      name: 'Import',
      action: () => handleImportClick()
    }
  ];

  const filterOptions = {
    filters: [
      {
        options: optionsToFilterTemplate.map(option => {
          if (filters?.filterTemplates?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Hierarchy',
        values: filters?.filterTemplates,
        onChange: templates => dispatch(updateSurveyFilters({ filterTemplates: templates, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductNames.map(option => {
          if (filters?.filterProductNames?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Name',
        values: filters?.filterProductNames,
        onChange: productNames =>
          dispatch(updateSurveyFilters({ filterProductNames: productNames, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductCategories.map(option => {
          if (filters?.filterProductCategories?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Category',
        values: filters?.filterProductCategories,
        onChange: productCategories =>
          dispatch(updateSurveyFilters({ filterProductCategories: productCategories, pageNo: 0 }))
      }
    ],
    resetFilters: () => dispatch(resetSurveyFilters())
  };

   const searchOptions = {
    searchPlaceholder: 'Search Surveys',
    searchAction: (payload) => dispatch(updateSurveyFilters(payload)),
    searchKey: 'searchInput',
    searchValue: filters?.searchInput || ''
  };

  const onEdit = row =>
    onClickEdit(
      [unsetCurrentSurvey(), setCurrentSurvey(row)],
      `/surveys/details/${row.id}`
    );
  const onCopy = id =>
    onClickCopy(setCopyCurrentSurvey, `/surveys/details`, id, surveys);
  return hasReadPermission ? (
    <div className={classes.root}>
      <ToolbarV2
        searchOptions={searchOptions}
        filterOptions={filterOptions}
        buttonsGroup={getButtonsGroup()}
        action={getPaginatedSurveys({ force: true })}
      />
      <div className={classes.content}>
        <TableComponentV2
          isLoading={isLoading}
          tableTitles={surveyTableTitles}
          onDoubleClickRow={row => onEdit(row)}
          actionButtonOptions={row => getActionButtons(row)}
          filteredRows={surveys}
          action={getPaginatedSurveys({ force: true })}
          filterValues={filters}
          filterAction={(payload) => dispatch(updateSurveyFilters(payload))}
        />
      </div>
      {openCopyTemplateModal && (
        <CopyTemplateModal
          isOpen={openCopyTemplateModal}
          closeModal={() => setOpenCopyTemplateModal(false)}
        />
      )}
    </div>
  ) : (
    <Typography align="center" color="error" style={{ margin: '25%' }}>
      Permission is required to view Surveys
    </Typography>
  );
};

export default Surveys;
